import React, { useMemo } from 'react';
import { Button, Dropdown, Flex, List, Thumbnail, token } from '@bynder/design-system';
import { IconAddLink, IconCheck, IconPlaceholder, IconUnlink } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import MaybeTooltip from 'packages/common/MaybeTooltip';
import generateTestId from '~/helpers/testIdHelpers';
import { ContentPropertyProps } from './types';
import { FullWidthBox, StyledListItem } from './ContentProperty.styled';
import { useContentProperty } from './useContentProperty';
import { useContentPropertyCreateModal } from './useContentPropertyCreateModal';
import ContextMenu from './components/ContextMenu';
import { useContentPropertyDeleteModal } from './useContentPropertyDeleteModal';
import { useContentPropertyRenameModal } from './useContentPropertyRenameModal';
import { useGenerateIcon } from './components/useGenerateIcon';
import { useContentPropertyListModal } from './useContentPropertyListModal';
import { useContentPropertyDetachModal } from './useContentPropertyDetachModal';
import { StyledSidebarDropdown } from 'packages/pages/editor/RightSideMenu/index.styled';

const ContentProperty = ({ creativeModel, selectedElements, disabled = false }: ContentPropertyProps) => {
    const { translate } = useTranslate();
    const { generateIcon } = useGenerateIcon({});

    const { selection, unlockedElements, primaryElement, allLocked, isMultiSelect } = useMemo(() => {
        const sel = [...selectedElements];
        const unlocked = sel.filter((el) => !el?.locked);

        return {
            selection: sel,
            unlockedElements: unlocked,
            primaryElement: sel[0],
            allLocked: unlocked.length === 0,
            isMultiSelect: sel.length > 1,
        };
    }, [selectedElements.map((el) => `${el.id}_${el?.contentPropertyId ?? ''}:${el?.locked ? 1 : 0}`).join('|')]);

    const contentPropertyIds = (!isMultiSelect && allLocked ? selection : unlockedElements)
        .map((el) => el?.contentPropertyId)
        .filter(Boolean);

    const sharedContentPropertyIds = selection
        .filter((el) => !el.locked)
        .map((el) => el?.contentPropertyId)
        .filter((id) => Boolean(id) && id !== '');

    const {
        allTypesProperties,
        currentProperty,
        filteredProperties,
        searchValue,
        handleSearchChange,
        applyProperty,
        onCreate,
        onDetach,
        onRename,
        onDelete,
        onSave,
    } = useContentProperty(creativeModel, selection);

    const { renderContentPropertyCreateModal, setIsContentPropertyCreateModalOpen } = useContentPropertyCreateModal({
        onCreate,
        elementName: isMultiSelect ? '' : (primaryElement?.name ?? ''),
        selectedElements: selection,
    });

    const { renderContentPropertyRenameModal, setIsContentPropertyRenameModalOpen } = useContentPropertyRenameModal({
        onRename,
        propertyName: currentProperty?.name ?? '',
    });

    const { renderContentPropertyDeleteModal, setIsContentPropertyDeleteModalOpen } = useContentPropertyDeleteModal({
        onDelete,
    });

    const { renderContentPropertyListModal, setIsContentPropertyListModalOpen } = useContentPropertyListModal({
        creativeModel,
        properties: allTypesProperties,
        onSave,
    });

    const { renderContentPropertyDetachModal, setIsContentPropertyDetachModalOpen } = useContentPropertyDetachModal({
        onDetach,
        selectedCount: sharedContentPropertyIds.length,
    });

    const handlePropertySelect = (item: any) => {
        if (!sharedContentPropertyIds.includes(item.uuid)) {
            applyProperty(primaryElement, item.uuid, true);
        }
    };

    const handleCreateClick = () => setIsContentPropertyCreateModalOpen(true);

    const handleDetachClick = isMultiSelect ? () => setIsContentPropertyDetachModalOpen(true) : onDetach;

    const renderDropdownContent = () => (
        <>
            <Dropdown.SearchInput
                value={searchValue}
                onChange={handleSearchChange}
                placeholder={translate('editor.sidebar.shots.contentProperty.search.placeholder')}
                aria-label={translate('editor.sidebar.shots.contentProperty.search.placeholder')}
                {...generateTestId('shots_content_property_search')}
                {...{ id: 'shots_content_property_search' }}
            />
            {!filteredProperties.length ? (
                <Dropdown.Text
                    {...generateTestId('shots_content_property_no_items')}
                    {...{ id: 'shots_content_property_no_items' }}
                >
                    {translate('editor.sidebar.shots.contentProperty.noItems')}
                </Dropdown.Text>
            ) : (
                filteredProperties.map((item) => (
                    <Dropdown.ListItem
                        key={item.uuid}
                        isChecked={false}
                        onClick={() => handlePropertySelect(item)}
                        thumbnail={generateIcon(item, false)}
                        rightElements={
                            sharedContentPropertyIds.includes(item.uuid) ? (
                                <IconCheck style={{ color: token.gray800 }} />
                            ) : null
                        }
                        {...generateTestId('shots_content_property_item')}
                        {...{ id: 'shots_content_property_item' }}
                    >
                        {item.name}
                    </Dropdown.ListItem>
                ))
            )}
            <Dropdown.Divider />
            <Dropdown.Item
                icon={<IconAddLink />}
                onClick={handleCreateClick}
                {...generateTestId('shots_content_property_create_option')}
                {...{ id: 'shots_content_property_create_option' }}
            >
                {translate('editor.sidebar.shots.contentProperty.createOption')}
            </Dropdown.Item>
        </>
    );

    const renderSingleSelectDropdown = () => (
        <Dropdown
            minWidth="288px"
            trigger={({ isOpen, ...triggerProps }) => (
                <List.Item
                    rightElements={<Dropdown.Arrow />}
                    isDisabled={allLocked || disabled}
                    thumbnail={generateIcon(primaryElement, true)}
                    {...generateTestId('shots_content_property_open_list')}
                    {...{ id: 'shots_content_property_open_list' }}
                    {...triggerProps}
                >
                    {currentProperty ? currentProperty?.name : (primaryElement?.name ?? '')}
                </List.Item>
            )}
        >
            {renderDropdownContent()}
        </Dropdown>
    );

    const renderMultiSelectItem = () => {
        const isSelectionWithContentProperty = selection.some((el) => el.contentPropertyId);
        const labelKey = isSelectionWithContentProperty
            ? 'editor.sidebar.shots.contentProperty.manageOption.multiple'
            : 'editor.sidebar.shots.contentProperty.createOption.multiple';

        return isSelectionWithContentProperty ? (
            <StyledSidebarDropdown
                minWidth="323px"
                trigger={({ isOpen, ...triggerProps }) => (
                    <StyledListItem
                        isDisabled={allLocked || disabled}
                        thumbnail={<Thumbnail variant="clean" icon={<IconPlaceholder />} />}
                        rightElements={<Dropdown.Arrow />}
                        {...generateTestId('shots_content_property_multiple_selected')}
                        {...{ id: 'shots_content_property_multiple_selected' }}
                        {...triggerProps}
                    >
                        {translate(labelKey)}
                    </StyledListItem>
                )}
            >
                {contentPropertyIds.length !== selection.length && (
                    <>
                        <Dropdown.Item onClick={() => setIsContentPropertyCreateModalOpen(true)} icon={<IconAddLink />}>
                            {translate('editor.sidebar.shots.contentProperties.context.create')}
                        </Dropdown.Item>
                        <Dropdown.Divider />
                    </>
                )}
                <Dropdown.Item onClick={() => setIsContentPropertyDetachModalOpen(true)} icon={<IconUnlink />}>
                    {translate('editor.sidebar.shots.contentProperties.context.detach')}
                </Dropdown.Item>
            </StyledSidebarDropdown>
        ) : (
            <>
                <FullWidthBox marginRight="6">
                    <StyledListItem
                        isDisabled={allLocked || disabled}
                        thumbnail={<Thumbnail variant="clean" icon={<IconPlaceholder />} />}
                        {...generateTestId('shots_content_property_multiple_selected')}
                        {...{ id: 'shots_content_property_multiple_selected' }}
                    >
                        {translate(labelKey)}
                    </StyledListItem>
                </FullWidthBox>
                {renderActionButton()}
            </>
        );
    };

    const renderActionButton = () => {
        if (contentPropertyIds.length) {
            return (
                <ContextMenu
                    selectedElements={selectedElements}
                    renameContentProperty={() => setIsContentPropertyRenameModalOpen(true)}
                    detachContentProperty={handleDetachClick}
                    manageContentProperty={() => setIsContentPropertyListModalOpen(true)}
                    deleteContentProperty={() => setIsContentPropertyDeleteModalOpen(true)}
                    createContentProperty={onCreate}
                />
            );
        }

        return (
            <MaybeTooltip
                hasTooltip={!allLocked && !disabled}
                position="bottom-right"
                content={translate('editor.sidebar.shots.contentProperty.tooltip')}
                {...generateTestId('shots_content_property_button_create_tooltip')}
                {...{ id: 'shots_content_property_button_create_tooltip' }}
            >
                <Button
                    variant="clean"
                    title={translate('editor.sidebar.shots.contentProperty.tooltip')}
                    icon={<IconAddLink />}
                    isDisabled={allLocked || disabled}
                    onClick={handleCreateClick}
                    {...generateTestId('shots_content_property_button_create')}
                    {...{ id: 'shots_content_property_button_create' }}
                />
            </MaybeTooltip>
        );
    };

    return (
        <>
            <Flex alignItems="center">
                {isMultiSelect ? (
                    renderMultiSelectItem()
                ) : (
                    <FullWidthBox marginRight="6">{renderSingleSelectDropdown()}</FullWidthBox>
                )}
                {!isMultiSelect && renderActionButton()}
            </Flex>
            {renderContentPropertyRenameModal()}
            {renderContentPropertyDeleteModal()}
            {renderContentPropertyCreateModal()}
            {renderContentPropertyListModal()}
            {renderContentPropertyDetachModal()}
        </>
    );
};

export default ContentProperty;
